/* ─────────────────────────────────────────────────────────────────────────
   Sassy Line PDP behaviours (sections/main-product-sassy.liquid).
   - Option pills (jar) + option cards (scent) drive variant matching
     against the embedded variants JSON, same way a standard variant
     picker does — this template supports up to two product options.
   - Gallery: thumbnail clicks swap the active stage panel; picking a jar
     swatch also swaps to that variant's own photo when it has one.
   - Add to cart goes through the same /cart/add.js + window.PavosCart
     flow as the rest of the theme, so the cart drawer, item count, and
     free-shipping meter all stay in sync — this template does not
     reinvent cart behaviour, only the PDP chrome around it.
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var ADDED_FLASH_MS = 2000;

  function readVariants(root) {
    var el = root.querySelector('[data-product-json]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function activatePanel(root, mediaId) {
    var panels = root.querySelectorAll('[data-stage-panel]');
    var thumbs = root.querySelectorAll('[data-thumb]');
    panels.forEach(function (panel) {
      var active = panel.getAttribute('data-media-id') === String(mediaId);
      panel.classList.toggle('is-active', active);
    });
    thumbs.forEach(function (thumb) {
      var active = thumb.getAttribute('data-media-id') === String(mediaId);
      thumb.classList.toggle('is-active', active);
    });
  }

  function formatMoney(cents, format) {
    var rupees = cents / 100;
    var hasPaise = Math.round(cents) % 100 !== 0;
    var amount;
    try {
      amount = rupees.toLocaleString('en-IN', {
        minimumFractionDigits: hasPaise ? 2 : 0,
        maximumFractionDigits: 2
      });
    } catch (e) {
      amount = hasPaise ? rupees.toFixed(2) : String(Math.round(rupees));
    }
    return (format || '₹{{amount}}').replace('{{amount}}', amount);
  }

  function initSection(root) {
    if (!root || root.hasAttribute('data-pdp-sassy-ready')) return;
    root.setAttribute('data-pdp-sassy-ready', '');

    var variants = readVariants(root);
    var moneyFormat = root.getAttribute('data-money-format') || '₹{{amount}}';

    var swatchGroup = root.querySelector('[data-swatch-group]');
    var scentGroup = root.querySelector('[data-scent-group]');
    var idInput = root.querySelector('[data-variant-id-input]');
    var priceEls = root.querySelectorAll('[data-price]');
    var compareEls = root.querySelectorAll('[data-compare]');
    var addButtons = root.querySelectorAll('[data-add-to-cart]');
    var microcopy = root.querySelector('[data-microcopy]');
    var qtyEl = root.querySelector('[data-qty-value]');
    var qtyInput = root.querySelector('[data-quantity-input]');
    var form = root.querySelector('[data-product-form]');

    var qty = 1;

    function currentSelection() {
      var sel1 = swatchGroup
        ? swatchGroup.querySelector('.pdp-sassy__swatch.is-selected')
        : null;
      var sel2 = scentGroup
        ? scentGroup.querySelector('.pdp-sassy__fragrance-option.is-selected')
        : null;
      return {
        option1: sel1 ? sel1.getAttribute('data-value') : null,
        option2: sel2 ? sel2.getAttribute('data-value') : null
      };
    }

    function findVariant() {
      if (!variants.length) return null;
      var sel = currentSelection();
      for (var i = 0; i < variants.length; i += 1) {
        var v = variants[i];
        var matches1 = sel.option1 === null || v.option1 === sel.option1;
        var matches2 = sel.option2 === null || v.option2 === sel.option2;
        if (matches1 && matches2) return v;
      }
      return null;
    }

    function applyVariant(variant) {
      if (!variant) return;

      if (idInput) idInput.value = variant.id;

      priceEls.forEach(function (node) {
        node.textContent = formatMoney(variant.price, moneyFormat);
      });

      compareEls.forEach(function (node) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          node.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          node.hidden = false;
        } else {
          node.hidden = true;
        }
      });

      addButtons.forEach(function (btn) {
        btn.disabled = !variant.available;
        var label = variant.available
          ? btn.getAttribute('data-label')
          : btn.getAttribute('data-soldout-label');
        var priceSpan = btn.querySelector('[data-add-price]');
        if (priceSpan) {
          priceSpan.textContent = variant.available
            ? formatMoney(variant.price, moneyFormat)
            : '';
        } else if (label) {
          btn.textContent = label;
        }
      });

      if (microcopy) {
        microcopy.classList.toggle('is-soldout', !variant.available);
        microcopy.textContent = variant.available
          ? microcopy.getAttribute('data-in-stock-text') || microcopy.textContent
          : microcopy.getAttribute('data-soldout-text') || 'Currently unavailable';
      }

      if (variant.featured_media && variant.featured_media.id) {
        activatePanel(root, variant.featured_media.id);
      }
    }

    function selectInGroup(group, className, target, ariaAttr) {
      if (!group) return;
      var attr = ariaAttr || 'aria-pressed';
      var nodes = group.querySelectorAll('.' + className);
      nodes.forEach(function (node) {
        var selected = node === target;
        node.classList.toggle('is-selected', selected);
        node.setAttribute(attr, selected ? 'true' : 'false');
      });
    }

    if (swatchGroup) {
      swatchGroup.querySelectorAll('[data-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-soldout')) return;
          selectInGroup(swatchGroup, 'pdp-sassy__swatch', btn, 'aria-checked');
          var picked = root.querySelector('[data-jar-pick]');
          if (picked) picked.textContent = btn.getAttribute('data-value');
          applyVariant(findVariant());
        });
      });
    }

    /* ── Fragrance dropdown (trigger + listbox panel) ─────────────────── */
    var fragranceWrap = root.querySelector('[data-fragrance-dropdown]');
    var fragranceTrigger = root.querySelector('[data-fragrance-trigger]');
    var fragrancePanel = root.querySelector('[data-fragrance-panel]');

    function closeFragrancePanel() {
      if (!fragrancePanel || !fragranceTrigger) return;
      fragrancePanel.hidden = true;
      fragranceTrigger.setAttribute('aria-expanded', 'false');
    }

    function openFragrancePanel() {
      if (!fragrancePanel || !fragranceTrigger) return;
      fragrancePanel.hidden = false;
      fragranceTrigger.setAttribute('aria-expanded', 'true');
    }

    if (fragranceTrigger && fragrancePanel) {
      fragranceTrigger.addEventListener('click', function () {
        var isOpen = fragranceTrigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          closeFragrancePanel();
        } else {
          openFragrancePanel();
        }
      });

      document.addEventListener('click', function (event) {
        if (fragranceWrap && !fragranceWrap.contains(event.target)) {
          closeFragrancePanel();
        }
      });

      fragranceWrap.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closeFragrancePanel();
          fragranceTrigger.focus();
        }
      });
    }

    if (scentGroup) {
      scentGroup.querySelectorAll('[data-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.classList.contains('is-soldout')) return;
          selectInGroup(scentGroup, 'pdp-sassy__fragrance-option', btn, 'aria-selected');

          var nameEl = btn.querySelector('.pdp-sassy__fragrance-option-name');
          var familyEl = btn.querySelector('.pdp-sassy__fragrance-option-family');
          var triggerName = root.querySelector('[data-fragrance-trigger-name]');
          var triggerFamily = root.querySelector('[data-fragrance-trigger-family]');

          if (triggerName && nameEl) triggerName.textContent = nameEl.textContent;
          if (triggerFamily) {
            if (familyEl) {
              triggerFamily.textContent = familyEl.textContent;
              triggerFamily.hidden = false;
            } else {
              triggerFamily.hidden = true;
            }
          }

          var throwBars = root.querySelectorAll('[data-throw-bars] span');
          var throwLabel = root.querySelector('[data-throw-label]');
          var throwLevel = parseInt(btn.getAttribute('data-throw-level'), 10);
          if (throwBars.length && !isNaN(throwLevel)) {
            throwBars.forEach(function (bar, i) {
              bar.classList.toggle('is-on', i < throwLevel);
            });
          }
          if (throwLabel) {
            var throwLabelText = btn.getAttribute('data-throw-label');
            if (throwLabelText) throwLabel.textContent = throwLabelText;
          }

          closeFragrancePanel();
          applyVariant(findVariant());
        });
      });
    }

    /* ── Gallery thumbs ─────────────────────────────────────────────── */
    root.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activatePanel(root, thumb.getAttribute('data-media-id'));
      });
    });

    /* ── Quantity stepper ───────────────────────────────────────────── */
    root.querySelectorAll('[data-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var delta = parseInt(btn.getAttribute('data-qty-step'), 10) || 0;
        qty = Math.max(1, qty + delta);
        if (qtyEl) qtyEl.textContent = String(qty);
        if (qtyInput) qtyInput.value = String(qty);
      });
    });

    /* ── Add to cart ────────────────────────────────────────────────── */
    function flashAdded() {
      addButtons.forEach(function (btn) {
        var addedLabel = btn.getAttribute('data-added-label');
        var priceSpan = btn.querySelector('[data-add-price]');
        if (addedLabel && !priceSpan) btn.textContent = addedLabel;
      });
      window.setTimeout(function () {
        applyVariant(findVariant());
      }, ADDED_FLASH_MS);
    }

    function addToCart(event) {
      if (event) event.preventDefault();
      var variant = findVariant();
      if (!variant || !variant.available) return;

      addButtons.forEach(function (btn) { btn.disabled = true; });

      var payload = { items: [{ id: variant.id, quantity: qty }] };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add-to-cart failed');
          return response.json();
        })
        .then(function (item) {
          if (window.PavosCart && typeof window.PavosCart.refreshDrawer === 'function') {
            return window.PavosCart.refreshDrawer().then(function () {
              document.dispatchEvent(new CustomEvent('pavos:cart:added', { detail: item }));
            });
          }
          return null;
        })
        .then(function () {
          addButtons.forEach(function (btn) { btn.disabled = false; });
          flashAdded();
        })
        .catch(function () {
          addButtons.forEach(function (btn) { btn.disabled = false; });
        });
    }

    addButtons.forEach(function (btn) {
      btn.addEventListener('click', addToCart);
    });

    if (form) {
      form.addEventListener('submit', addToCart);
    }

    /* Initial state */
    applyVariant(findVariant());
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-pdp-sassy]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
